import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Owner, OwnerContact } from '../types';
import { API_URL } from '../config';
import './Owners.css';

const Owners: React.FC = () => {
  const { user } = useAuth();
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);

  const fetchOwners = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/api/owners`, config);
      setOwners(response.data.owners || []);
    } catch (error: any) {
      console.error('Failed to fetch owners:', error);
      if (error.response?.status === 403) {
        // Access denied - will be handled by axios interceptor
        return;
      }
      setOwners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleDeleteOwner = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/owners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOwners();
    } catch (error: any) {
      console.error('Failed to delete owner:', error);
      alert(error.response?.data?.message || 'Failed to delete owner');
    }
  };

  if (loading) {
    return <div className="owners-loading">Loading...</div>;
  }

  return (
    <div className="owners-page">
      <header className="owners-header">
        <div className="header-content">
          <h1>Building Owners</h1>
          <p>Contact information and ownership details for 18 Graf Road</p>
        </div>
        {user?.role === 'admin' && (
          <button className="add-owner-btn" onClick={() => {
            setEditingOwner(null);
            setShowOwnerModal(true);
          }}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Owner
          </button>
        )}
      </header>

      <div className="owners-grid">
        {owners.map((owner) => (
          <OwnerCard
            key={owner._id}
            owner={owner}
            isAdmin={user?.role === 'admin'}
            onEdit={() => {
              setEditingOwner(owner);
              setShowOwnerModal(true);
            }}
            onDelete={() => handleDeleteOwner(owner._id)}
          />
        ))}
        {owners.length === 0 && (
          <div className="no-owners">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
            <p>No owners found</p>
          </div>
        )}
      </div>

      {showOwnerModal && (
        <OwnerModal
          owner={editingOwner}
          onClose={() => {
            setShowOwnerModal(false);
            setEditingOwner(null);
          }}
          onSuccess={() => {
            setShowOwnerModal(false);
            setEditingOwner(null);
            fetchOwners();
          }}
        />
      )}
    </div>
  );
};

// Owner Card Component
const OwnerCard: React.FC<{
  owner: Owner;
  isAdmin?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ owner, isAdmin, onEdit, onDelete }) => {
  return (
    <article className="owner-card">
      <div className="card-header">
        <div className="unit-badge">Unit {owner.unit}</div>
        {owner.ownershipPercentage !== undefined && owner.ownershipPercentage > 0 && (
          <div className="ownership-badge">{owner.ownershipPercentage}%</div>
        )}
      </div>

      <h3 className="owner-name">{owner.displayName}</h3>

      {owner.notes && (
        <p className="owner-notes">{owner.notes}</p>
      )}

      <div className="contacts-section">
        {owner.contacts.map((contact, idx) => (
          <div key={idx} className={`contact-item${contact.isPrimary ? ' primary' : ''}`}>
            <div className="contact-name">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span>{owner.contacts.length > 1 ? `Owner ${idx + 1} — ` : ''}{contact.name || 'Unnamed'}</span>
            </div>
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="contact-detail email">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                {contact.email}
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="contact-detail phone">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {contact.phone}
              </a>
            )}
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="card-actions">
          <button className="edit-btn" onClick={onEdit}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit
          </button>
          <button className="delete-btn" onClick={onDelete}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Delete
          </button>
        </div>
      )}
    </article>
  );
};

/**
 * Detects if a string contains "&" or " and " suggesting multiple people,
 * and splits it into separate names. Returns null if no split is detected.
 */
const detectSplittableNames = (value: string): string[] | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const separatorRegex = /\s+&\s+|\s+and\s+/i;
  if (!separatorRegex.test(trimmed)) return null;

  const parts = trimmed.split(separatorRegex).map(s => s.trim()).filter(Boolean);
  return parts.length >= 2 ? parts : null;
};

/**
 * Detects if an email field contains multiple emails separated by
 * common delimiters (& , ; / "and") and returns them.
 */
const detectSplittableEmails = (value: string): string[] | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const separatorRegex = /[,;&/]\s*|\s+and\s+/i;
  if (!separatorRegex.test(trimmed)) return null;

  const parts = trimmed.split(separatorRegex).map(s => s.trim()).filter(s => s.includes('@'));
  return parts.length >= 2 ? parts : null;
};

// Owner Modal Component
const OwnerModal: React.FC<{
  owner: Owner | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ owner, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    displayName: owner?.displayName || '',
    unit: owner?.unit || '',
    ownershipPercentage: owner?.ownershipPercentage || '',
    notes: owner?.notes || '',
  });
  
  const [contacts, setContacts] = useState<OwnerContact[]>(
    owner?.contacts && owner.contacts.length > 0 
      ? owner.contacts 
      : [{ name: '', email: '', phone: '', isPrimary: true }]
  );

  const [splitSuggestion, setSplitSuggestion] = useState<{
    index: number;
    field: 'name' | 'email';
    parts: string[];
  } | null>(null);

  const addContact = () => {
    setContacts([...contacts, { name: '', email: '', phone: '', isPrimary: false }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= 1) return;
    const updated = contacts.filter((_, i) => i !== index);
    if (!updated.some(c => c.isPrimary) && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    setContacts(updated);
  };

  const updateContact = (index: number, field: keyof OwnerContact, value: string | boolean) => {
    const updated = [...contacts];
    if (field === 'isPrimary' && value === true) {
      updated.forEach((c, i) => {
        c.isPrimary = i === index;
      });
    } else {
      (updated[index] as any)[field] = value;
    }
    setContacts(updated);

    if (field === 'name' && typeof value === 'string') {
      const names = detectSplittableNames(value);
      if (names) {
        setSplitSuggestion({ index, field: 'name', parts: names });
      } else if (splitSuggestion?.index === index && splitSuggestion?.field === 'name') {
        setSplitSuggestion(null);
      }
    }
    if (field === 'email' && typeof value === 'string') {
      const emails = detectSplittableEmails(value);
      if (emails) {
        setSplitSuggestion({ index, field: 'email', parts: emails });
      } else if (splitSuggestion?.index === index && splitSuggestion?.field === 'email') {
        setSplitSuggestion(null);
      }
    }
  };

  const applySplit = () => {
    if (!splitSuggestion) return;
    const { index, field, parts } = splitSuggestion;
    const sourceContact = contacts[index];

    const newContacts = [...contacts];
    if (field === 'name') {
      newContacts[index] = { ...sourceContact, name: parts[0] };
      for (let i = 1; i < parts.length; i++) {
        newContacts.splice(index + i, 0, {
          name: parts[i],
          email: '',
          phone: '',
          isPrimary: false,
        });
      }
    } else if (field === 'email') {
      newContacts[index] = { ...sourceContact, email: parts[0] };
      for (let i = 1; i < parts.length; i++) {
        newContacts.splice(index + i, 0, {
          name: '',
          email: parts[i],
          phone: '',
          isPrimary: false,
        });
      }
    }

    setContacts(newContacts);
    setSplitSuggestion(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validContacts = contacts.filter(c => c.name.trim() || c.email?.trim() || c.phone?.trim());

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        displayName: formData.displayName || '',
        unit: formData.unit || '',
        contacts: validContacts.length > 0 ? validContacts : [],
        ownershipPercentage: formData.ownershipPercentage && String(formData.ownershipPercentage).trim() !== '' ? Number(formData.ownershipPercentage) : null,
        notes: formData.notes || '',
      };

      if (owner?._id) {
        await axios.put(`${API_URL}/api/owners/${owner._id}`, payload, config);
      } else {
        await axios.post(`${API_URL}/api/owners`, payload, config);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save owner:', error);
      alert(error.response?.data?.message || 'Failed to save owner');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{owner?._id ? 'Edit Owner' : 'Add New Owner'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="e.g., John Smith or Smith & Jones LLC"
                  className={!formData.displayName.trim() ? 'empty-field' : ''}
                />
              </div>
              <div className="form-group small">
                <label>Unit</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="e.g., 2A"
                  className={!formData.unit.trim() ? 'empty-field' : ''}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group small">
                <label>Ownership %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.ownershipPercentage}
                  onChange={(e) => setFormData({ ...formData, ownershipPercentage: e.target.value })}
                  placeholder="e.g., 5"
                  className={!formData.ownershipPercentage || String(formData.ownershipPercentage).trim() === '' ? 'empty-field' : ''}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., Airpark Ownership"
                  className={!formData.notes.trim() ? 'empty-field' : ''}
                />
              </div>
            </div>
          </div>

          <div className="form-section contacts-section-form">
            <div className="section-header-row">
              <h3>Owners / Contacts</h3>
              <button type="button" className="add-contact-btn" onClick={addContact}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Contact
              </button>
            </div>

            <div className="contacts-scroll-area">
              {contacts.map((contact, index) => (
                <div key={index} className="contact-form-group">
                  <div className="contact-header">
                    <span className="owner-label">Contact {index + 1}</span>
                    <div className="contact-header-actions">
                      <label className="primary-checkbox">
                        <input
                          type="radio"
                          name="primaryContact"
                          checked={contact.isPrimary}
                          onChange={() => updateContact(index, 'isPrimary', true)}
                        />
                        <span>Primary</span>
                      </label>
                      {contacts.length > 1 && (
                        <button 
                          type="button" 
                          className="remove-contact-btn"
                          onClick={() => removeContact(index)}
                          title="Remove contact"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {splitSuggestion && splitSuggestion.index === index && (
                    <div className="split-suggestion">
                      <p>
                        It looks like this {splitSuggestion.field} contains multiple people:{' '}
                        <strong>{splitSuggestion.parts.join(', ')}</strong>
                      </p>
                      <div className="split-actions">
                        <button type="button" className="split-btn" onClick={applySplit}>
                          Split into {splitSuggestion.parts.length} contacts
                        </button>
                        <button type="button" className="split-dismiss" onClick={() => setSplitSuggestion(null)}>
                          Keep as-is
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="form-row three-col">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        placeholder="Full name"
                        className={!contact.name.trim() ? 'empty-field' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={contact.email || ''}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className={!contact.email?.trim() ? 'empty-field' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={contact.phone || ''}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="(555) 555-5555"
                        className={!contact.phone?.trim() ? 'empty-field' : ''}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {owner?._id ? 'Save Changes' : 'Create Owner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Owners;
